// app/javascript/controllers/chatbox_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", "file", "preview", "recaptchaToken",
    "submitButton", "submitLabel", "submitSpinner",
    "messages", "contextUrl", "contextTitle"
  ]
  static values  = {
    uploadingCount: Number,
    recaptchaSiteKey: String,
    recaptchaAction: { type: String, default: "chat_message_create" },
    showSpinner: { type: Boolean, default: true }
  }

  connect() {
    this._objectUrls = []
    this.uploadingCountValue = 0
    this._pendingEl = null
    this.autoGrow()
    this.updateRequired()
    this.updateContext()
    this._bindDirectUploadEvents()

    if (!this.showSpinnerValue && this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.add("hidden")
    }
    if (!this.showSpinnerValue && this.hasSubmitLabelTarget) {
      this.submitLabelTarget.classList.remove("opacity-0")
    }
  }

  // --- UI helpers -----------------------------------------------------------
  autoGrow() {
    if (!this.hasInputTarget) return
    this.inputTarget.style.height = "auto"
    this.inputTarget.style.height = `${this.inputTarget.scrollHeight}px`
  }

  setLoading(on) {
    if (!this.hasSubmitButtonTarget) return

    const showSpinner = this.showSpinnerValue

    if (!showSpinner) {
      this.submitButtonTarget.disabled = false
      if (this.hasSubmitLabelTarget) this.submitLabelTarget.classList.remove("opacity-0")
      if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.add("hidden")
      return
    }

    this.submitButtonTarget.disabled = on

    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.classList.toggle("opacity-0", on)
    }

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.toggle("hidden", !on)
    }
  }

  // --- Text input -----------------------------------------------------------
  onInput() {
    this.autoGrow()
    this.updateRequired()
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.element.requestSubmit()
    }
  }

  // --- Files / previews -----------------------------------------------------
  openPicker() {
    if (this.hasFileTarget) this.fileTarget.click()
  }

  filesChosen() {
    if (!this.hasFileTarget) return
    this.renderPreviews(this.fileTarget.files)
    this.updateRequired()
  }

  renderPreviews(fileList) {
    if (!this.hasPreviewTarget) return
    this.clearPreviews()

    const files = Array.from(fileList || [])
    files.forEach((file, idx) => {
      const url = URL.createObjectURL(file)
      this._objectUrls.push(url)

      const wrapper = document.createElement("div")
      wrapper.className = "relative w-14 h-14 rounded overflow-hidden ring-1 ring-slate-200"

      const img = document.createElement("img")
      img.src = url
      img.alt = file.name
      img.className = "object-cover w-full h-full"
      wrapper.appendChild(img)

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/90 ring-1 ring-slate-300 text-slate-600 hover:bg-white"
      removeBtn.setAttribute("aria-label", "Remove image")
      removeBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-3.5 h-3.5">
          <path fill-rule="evenodd" d="M10 8.586 5.293 3.879A1 1 0 1 0 3.879 5.293L8.586 10l-4.707 4.707a1 1 0 1 0 1.414 1.414L10 11.414l4.707 4.707a1 1 0 0 0 1.414-1.414L11.414 10l4.707-4.707a1 1 0 0 0-1.414-1.414L10 8.586Z" clip-rule="evenodd" />
        </svg>`
      removeBtn.addEventListener("click", () => this.removeFileAt(idx))
      wrapper.appendChild(removeBtn)

      this.previewTarget.appendChild(wrapper)
    })
  }

  removeFileAt(index) {
    if (!this.hasFileTarget) return
    const dt = new DataTransfer()
    const files = Array.from(this.fileTarget.files)
    files.forEach((f, i) => { if (i !== index) dt.items.add(f) })
    this.fileTarget.files = dt.files
    this.renderPreviews(this.fileTarget.files)
    this.updateRequired()
  }

  clearPreviews() {
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = ""
    this._objectUrls.forEach(u => URL.revokeObjectURL(u))
    this._objectUrls = []
  }

  hasFiles() {
    return this.hasFileTarget && this.fileTarget.files && this.fileTarget.files.length > 0
  }

  // --- Required logic: allow photo-only messages ----------------------------
  updateRequired() {
    if (!this.hasInputTarget) return
    this.inputTarget.required = !this.hasFiles()
  }

  // --- Submit lifecycle ------------------------------------------------------
  async submit(event) {
    // fired by submit->chatbox#submit
    this.updateContext()
    if (this._skipRecaptchaOnce) {
      this._skipRecaptchaOnce = false
      return
    }

    if (this.shouldRunRecaptcha()) {
      if (event) event.preventDefault()
      if (this._recaptchaSubmitting) return
      this._recaptchaSubmitting = true
      this.setLoading(true)
      if (this.hasFiles()) this.showPendingUpload()

      const token = await this.fetchRecaptchaToken()
      if (this.hasRecaptchaTokenTarget) this.recaptchaTokenTarget.value = token || ""

      this._recaptchaSubmitting = false
      this._skipRecaptchaOnce = true
      this.element.requestSubmit()
      return
    }

    this.setLoading(true)
    if (this.hasFiles()) this.showPendingUpload()
  }

  // called by turbo:submit-end->chatbox#submitted
  async submitted() {
    // If uploads are still in-flight, keep button spinner + pending bubble
    if (this.uploadingCountValue > 0) return

    // Wait for images in the last appended message to load, then remove pending bubble
    await this.waitForNewestImagesToLoad()
    this.hidePendingUpload()

    // Clear inputs after successful submit
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.autoGrow()
    }
    if (this.hasFileTarget) this.fileTarget.value = ""
    this.clearPreviews()
    this.updateRequired()

    this.setLoading(false)
    if (this.hasRecaptchaTokenTarget) this.recaptchaTokenTarget.value = ""

    // Nudge autoscroll for the final layout after images are rendered
    document.dispatchEvent(new Event("turbo:before-stream-render"))
    requestAnimationFrame(() => {
      document.dispatchEvent(new Event("turbo:before-stream-render"))
    })
  }

  // --- Pending bubble in the message list -----------------------------------
  showPendingUpload() {
    if (!this.hasMessagesTarget || this._pendingEl) return

    const wrapper = document.createElement("div")
    wrapper.id = "chat_pending_upload"
    wrapper.className = "flex justify-start mt-4 mb-2"
    wrapper.innerHTML = `
      <div class="max-w-[85%] sm:max-w-[70%] w-full">
        <div class="flex flex-col gap-1 items-start w-full">
          <div class="w-full border-t border-slate-200 mb-2"></div>
          <div class="flex items-start gap-2">
            <span aria-hidden="true" class="text-slate-400 select-none leading-6 pl-3 opacity-60">└─</span>
            <div class="px-2.5 py-2 text-[13px] leading-snug flex items-center gap-2 bg-white text-slate-900 ring-1 ring-slate-200">
              <svg class="animate-spin h-4 w-4 text-yellow-600" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-90" fill="currentColor"
                      d="M4 12a8 8 0 018-8v4A4 4 0 0 1 8 12H4z"></path>
              </svg>
              <span>Uploading photos…</span>
            </div>
          </div>
        </div>
      </div>`
    this._pendingEl = wrapper
    this.messagesTarget.appendChild(wrapper)

    // scroll to bottom immediately
    this.messagesTarget.scrollTo({ top: this.messagesTarget.scrollHeight, behavior: "auto" })
  }

  hidePendingUpload() {
    if (this._pendingEl && this._pendingEl.parentNode) {
      this._pendingEl.parentNode.removeChild(this._pendingEl)
      this._pendingEl = null
    }
  }

  // Wait until newly appended images in the messages container are loaded
  waitForNewestImagesToLoad() {
    return new Promise(resolve => {
      const container = this.hasMessagesTarget ? this.messagesTarget : null
      if (!container) return resolve()

      // Find images that are not yet complete
      const imgs = Array.from(container.querySelectorAll("img")).filter(img => !img.complete)
      if (imgs.length === 0) return resolve()

      let pending = imgs.length
      const done = () => { pending -= 1; if (pending <= 0) resolve() }
      imgs.forEach(img => {
        img.addEventListener("load", done, { once: true })
        img.addEventListener("error", done, { once: true })
      })

      // Safety timeout (in case of cached images that don't fire events)
      setTimeout(resolve, 1200)
    })
  }

  // --- Active Storage direct upload events ----------------------------------
  _bindDirectUploadEvents() {
    this.element.addEventListener("direct-upload:start", this._onDUStart)
    this.element.addEventListener("direct-upload:end",   this._onDUEnd)
  }

  _onDUStart = () => {
    this.uploadingCountValue = (this.uploadingCountValue || 0) + 1
    this.setLoading(true)
  }

  _onDUEnd = () => {
    this.uploadingCountValue = Math.max(0, (this.uploadingCountValue || 0) - 1)
    // Button spinner stays on until submitted() clears, because Turbo submit may still be finishing.
  }

  // --- Context (page title + URL) ------------------------------------------
  updateContext() {
    if (typeof window === "undefined") return
    const currentUrl = window.location?.href || ""
    const currentTitle = document?.title || ""
    if (this.hasContextUrlTarget) this.contextUrlTarget.value = currentUrl
    if (this.hasContextTitleTarget) this.contextTitleTarget.value = currentTitle
  }

  // --- reCAPTCHA v3 ---------------------------------------------------------
  shouldRunRecaptcha() {
    return this.hasRecaptchaTokenTarget && this.hasRecaptchaSiteKeyValue && !!this.recaptchaSiteKeyValue
  }

  fetchRecaptchaToken() {
    if (typeof window === "undefined") return Promise.resolve("")
    if (!this.hasRecaptchaSiteKeyValue || !this.recaptchaSiteKeyValue) return Promise.resolve("")
    if (typeof grecaptcha === "undefined" || !grecaptcha.execute || !grecaptcha.ready) {
      return Promise.resolve("")
    }

    return new Promise(resolve => {
      grecaptcha.ready(() => {
        grecaptcha.execute(this.recaptchaSiteKeyValue, { action: this.recaptchaActionValue })
          .then(token => resolve(token))
          .catch(() => resolve(""))
      })
    })
  }
}
